<template>
  <div class="line-chart">
    <svg :width="width" :height="height">
      <g :id="chartOptions.chartName + '__axis--x'" class="axis axis--x"></g>
      <g :id="chartOptions.chartName + '__axis--y'" class="axis axis--y"></g>
      <g :style="{transform: `translate(${margin.left}px, ${margin.top}px)`}">
        <path class="line" :d="line" />
      </g>
    </svg>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import isFunction from 'lodash/isFunction'
import identity from 'lodash/identity'
import * as d3 from 'd3';
import embeddable from '../mixins/embeddable'

// Call the first argument if it's a function, or return it
const castCall = (fnOrValue = identity, ...rest) => isFunction(fnOrValue) ? fnOrValue(...rest) : fnOrValue

const props = {
  chartData: Array,
  chartOptions: Object
}

export default {
  name: 'LineChart',
  mixins: [embeddable],
  props,
  data() {
    return {
      width: 0,
      height: 0,
      line: '',
      scale: {
        x: null,
        y: null,
      },
      points: []
    }
  },
  computed: {
    margin() {
      const left = this.chartOptions.labelWidth,
      right = 20,
      top = 30,
      bottom = 30;
      return {left, right, top, bottom}
    },
    padded() {
      const width = this.width - this.margin.left - this.margin.right;
      const height = this.height - this.margin.top - this.margin.bottom;
      return { width, height };
    },
    formattedData() {
      let formattedData = cloneDeep(this.chartData);
      formattedData.forEach(d => {
        d.date = this.parseTime(d.date);
        d[this.chartOptions.seriesName] = +d[this.chartOptions.seriesName];
      });

      return formattedData;
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    width: function widthChanged() {
      this.initialize();
      this.update();
    }
  },
  methods: {
    onResize() {
      this.width = this.$el.offsetWidth;
      this.height = this.$el.offsetWidth / 2;
    },
    createLine: d3.line().x(d => d.x).y(d => d.y),
    parseTime: d3.timeParse("%Y"),
    initialize() {
      this.scale.x = d3.scaleTime().range([0, this.padded.width]);
      this.scale.y = d3.scaleLinear().range([this.padded.height, 0]);
      d3.axisLeft().scale(this.scale.y);
      d3.axisBottom().scale(this.scale.x);
    },
    update() {
      this.scale.x.domain(d3.extent(this.formattedData, d => d.date));
      this.scale.y.domain([0, d3.max(this.formattedData, d => d[this.chartOptions.seriesName])]);
      this.points = [];
      for (const d of this.formattedData) {
        this.points.push({
          x: this.scale.x(d.date),
          y: this.scale.y(d[this.chartOptions.seriesName]),
        });
      }
      this.line = this.createLine(this.points);
      d3.select(this.$el).select(".axis--x")
        .call(d3.axisBottom(this.scale.x)
        .tickFormat(d => castCall(this.xAxisYearFormat, d.getFullYear())))
        .attr('transform', 'translate(' + this.margin.left + ', ' + (this.margin.top + this.padded.height) + ')');

      const ticks = this.chartOptions.yAxisTicks,
        tickFormat = this.chartOptions.yAxisTickFormat;

      d3.select(this.$el).select(".axis--y")
        .call(d3.axisLeft(this.scale.y)
        .tickFormat(d => tickFormat(d))
        .ticks(ticks))
        .attr('transform', 'translate(' + this.margin.left + ', ' + this.margin.top + ')')
        .selectAll(".tick line").attr("x2", this.padded.width);
    }
  }
}
</script>

<style scoped lang="scss">
  path {
    fill: none;
    stroke-width: 3px;
    stroke: $blumine;

    .graphic-container--social-mode & {
      stroke: $primary;
    }
  }
</style>
